import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'

import { EmailTypes } from 'enum'
import { Notification } from 'models/course.model'
import SendEmail from '../../components/send-email/SendEmail'
import { useForm } from '../../context'
import useEmailDefaultValues from '../../hooks/useEmailDefaultValues'
import styles from '../EmailNotifications.module.scss'
import NotificationChannelRow from './NotificationChannelRow'

interface Props {
  isNewCourse?: boolean
  disabled?: boolean
}

const EnrolCreatedNotification: React.FC<Props> = ({
  isNewCourse,
  disabled = false,
}) => {
  const { t } = useTranslation()
  const { values, setFieldValue } = useForm()

  const getTexts = useEmailDefaultValues()

  const texts = useMemo(
    () =>
      getTexts({
        // Same predefined copy as invitation / course_mail_sent CIO template.
        emailType: EmailTypes.invitation,
        courseName: values.name,
      }),
    [getTexts, values.name]
  )

  const value = values.enrolCreatedNotification
  const emailActive = !!value?.active
  const pushActive = !!value?.pushActive

  const onChange = (partial: Partial<Notification>) => {
    setFieldValue('enrolCreatedNotification', {
      active: false,
      pushActive: false,
      emailBody: texts.content,
      emailSubject: texts.subject,
      emailType: EmailTypes.enrolCreated,
      ...value,
      ...partial,
    })
  }

  const defaultValues = useMemo(
    () =>
      value || {
        emailBody: texts.content,
        emailSubject: texts.subject,
      },
    [value, texts]
  )

  return (
    <div
      className={styles.eventBlock}
      data-test-id="enrol-created-notification"
    >
      <div className={classNames(styles.eventTitle)}>
        {t('administration.courses.new.form.notifications.enrolCreated.title')}
      </div>
      <NotificationChannelRow
        channel="email"
        checked={emailActive}
        disabled={disabled}
        switchTestId="enrol-created-email-switch"
        onChange={(checked) => onChange({ active: checked })}
        trailing={
          <SendEmail
            defaultValues={defaultValues}
            canEdit={!disabled}
            onSave={({ emailBody, emailSubject }) => {
              onChange({ emailBody, emailSubject })
            }}
            isNewCourse={isNewCourse}
            courseName={values.name}
          />
        }
      />
      <NotificationChannelRow
        channel="mobile"
        checked={pushActive}
        disabled={disabled}
        switchTestId="enrol-created-mobile-switch"
        onChange={(checked) => onChange({ pushActive: checked })}
      />
    </div>
  )
}

export default EnrolCreatedNotification
